import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import iwalkcvService from '../../services/iwalkcv.service';

interface Disponibilidade {
  id: number;
  exp_titulo?: string;
  experiencia_id: number;
  data: string;
  hora: string;
  vagas_total: number;
  vagas_reservadas: number;
  activo: number;
}

interface ExperienciaOption {
  id: number;
  titulo: string;
}

const IWalkCVDisponibilidades: React.FC = () => {
  const [disponibilidades, setDisponibilidades] = useState<Disponibilidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [experiencias, setExperiencias] = useState<ExperienciaOption[]>([]);
  const [expFilter, setExpFilter] = useState<number | ''>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadExperiencias = async () => {
    try {
      const res = await iwalkcvService.getExperiencias({ limit: 100 });
      setExperiencias(res.data || res.experiencias || res);
    } catch (e) {
      console.error(e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await iwalkcvService.getDisponibilidades({
        experiencia_id: expFilter !== '' ? expFilter : undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setDisponibilidades(res.data || res.disponibilidades || res);
      setTotal(res.total || res.pagination?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadExperiencias(); }, []);
  useEffect(() => { load(); }, [expFilter, page, rowsPerPage]);

  const handleExpFilterChange = (value: number | '') => {
    setExpFilter(value);
    setPage(0);
  };

  const openDelete = (id: number) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    setDeleting(true);
    try {
      await iwalkcvService.deleteDisponibilidade(deletingId);
      setDeleteDialogOpen(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>Disponibilidades</Typography>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Filtrar por Experiência</InputLabel>
          <Select
            value={expFilter}
            label="Filtrar por Experiência"
            onChange={(e) => handleExpFilterChange(e.target.value as number | '')}
          >
            <MenuItem value="">Todas</MenuItem>
            {experiencias.map((exp) => (
              <MenuItem key={exp.id} value={exp.id}>{exp.titulo}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Experiência</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Hora</TableCell>
              <TableCell align="right">Vagas Total</TableCell>
              <TableCell align="right">Reservadas</TableCell>
              <TableCell align="right">Livres</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : disponibilidades.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma disponibilidade encontrada.
                </TableCell>
              </TableRow>
            ) : (
              disponibilidades.map((slot) => {
                const vagasLivres = slot.vagas_total - slot.vagas_reservadas;
                const canDelete = slot.vagas_reservadas === 0;
                return (
                  <TableRow key={slot.id} hover>
                    <TableCell>{slot.id}</TableCell>
                    <TableCell>{slot.exp_titulo || slot.experiencia_id}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {slot.data ? new Date(slot.data).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '—'}
                    </TableCell>
                    <TableCell>{slot.hora}</TableCell>
                    <TableCell align="right">{slot.vagas_total}</TableCell>
                    <TableCell align="right">{slot.vagas_reservadas}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={vagasLivres}
                        size="small"
                        color={vagasLivres === 0 ? 'error' : vagasLivres <= 3 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={slot.activo === 1 ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={slot.activo === 1 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={canDelete ? 'Eliminar slot' : 'Não é possível eliminar — existem reservas'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!canDelete}
                            onClick={() => openDelete(slot.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página:"
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          <Typography>Tem a certeza que pretende eliminar este slot de disponibilidade? Esta acção é irreversível.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IWalkCVDisponibilidades;
